import * as THREE from 'three';

/**
 * Build a `MeshStandardMaterial` whose PBR factors are appropriate for
 * lighting visualisation, intended for meshes that we hand to
 * `THREE.GLTFExporter` and later render through the WebGPU path tracer.
 *
 * Rationale for the chosen factors:
 *   - `metalness = 0` — floors, walls, ceilings are dielectric. A metallic
 *     surface routes all reflection through Fresnel and produces zero diffuse
 *     response, which is the opposite of what an illuminance reading on a
 *     real surface looks like.
 *   - `roughness = 1` — Lambertian-ish. The norm calculations elsewhere in
 *     the app (CHL / LMN, illuminance-based) implicitly assume a diffuse
 *     reflector, so the on-screen look should match.
 *   - `color = (0.7, 0.7, 0.7)` in *linear* space — ~70% albedo, in the
 *     range of a light interior surface. Bright enough to make illuminance
 *     distribution easy to read by eye, while leaving headroom before ACES
 *     saturation in the present pass.
 *   - `side = DoubleSide` — many generated meshes (thin walls, polyline
 *     shells) are visually expected to be two-sided.
 *
 * Why `new THREE.Color(0.7, 0.7, 0.7)` instead of `'#B3B3B3'`:
 * `THREE.Color` interprets hex strings (and `setHex`) as sRGB and converts
 * them to the linear working color space. The three-number constructor goes
 * through `setRGB` with `colorSpace = ColorManagement.workingColorSpace`
 * (LinearSRGB by default), so the values are stored as-is. `GLTFExporter`
 * writes `material.color.toArray()` straight into `baseColorFactor`, which
 * glTF 2.0 defines as linear — so this produces a correctly-encoded
 * `baseColorFactor = [0.7, 0.7, 0.7, 1.0]`. Going through a sRGB hex would
 * instead bake in `~0.21`, which the path tracer reads as near-black.
 */
export function createNeutralLightingMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    name: 'taman-neutral-diffuse',
    color: new THREE.Color(0.7, 0.7, 0.7),
    metalness: 0,
    roughness: 1,
    side: THREE.DoubleSide,
  });
}
